import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Loader2, AlertTriangle, FileText, X, Lock } from "lucide-react";
import { toast } from "sonner";

/**
 * LyricsModal — subscriber-only view for a song's transcript. Adds:
 *   - Copyright notice header
 *   - Per-user visible watermark
 *   - Invisible zero-width fingerprint (server-side, per user)
 *   - AI-generated notice + "Report an error" flow for ADA good-faith documentation
 *   - View log (server-side)
 * Non-subscribers get a paywall message. No copy-blocking (would break screen readers).
 */
export function LyricsModal({ open, onOpenChange, contentId, songTitle, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [locked, setLocked] = useState(null);
  const navigate = useNavigate();
  const [reporting, setReporting] = useState(false);
  const [reportNote, setReportNote] = useState("");
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    if (!open || !contentId) return;
    setLoading(true);
    setError(null);
    setLocked(null);
    setData(null);
    axios
      .get(`${API}/content/${contentId}/lyrics`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      .then((r) => setData(r.data))
      .catch((e) => {
        const s = e.response?.status;
        if (s === 401) setLocked("guest");
        else if (s === 402) setLocked("free");
        else if (s === 404) setError("Lyrics aren't ready for this song yet.");
        else setError(e.response?.data?.detail || "Could not load lyrics.");
      })
      .finally(() => setLoading(false));
  }, [open, contentId, token]);

  const submitReport = async () => {
    const note = reportNote.trim();
    if (!note) {
      toast.error("Please describe the error.");
      return;
    }
    setReporting(true);
    try {
      await axios.post(
        `${API}/content/${contentId}/lyrics/report-error`,
        { note },
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      toast.success("Thanks — we'll review and update.");
      setReportNote("");
      setReportOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send report.");
    } finally {
      setReporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
        data-testid="lyrics-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-600" />
            Lyrics — {songTitle}
          </DialogTitle>
          {data?.course_name && (
            <DialogDescription>{data.course_name}</DialogDescription>
          )}
        </DialogHeader>

        {/* Copyright notice — always visible */}
        {data && (
          <div className="rounded-md bg-purple-50 border border-purple-200 px-3 py-2 text-[11px] text-purple-900 leading-relaxed">
            {data.copyright_notice}
          </div>
        )}

        {/* AI-generated notice */}
        {data?.ai_generated && !reportOpen && (
          <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              These lyrics were auto-transcribed and may contain errors.{" "}
              <button
                type="button"
                onClick={() => setReportOpen(true)}
                className="underline font-medium hover:text-amber-950"
                data-testid="report-lyrics-error-btn"
              >
                Report an error
              </button>
            </div>
          </div>
        )}

        {/* Report form */}
        {reportOpen && (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-amber-900">
                Report a transcription error
              </p>
              <button
                type="button"
                onClick={() => setReportOpen(false)}
                className="text-amber-700 hover:text-amber-900"
                aria-label="Close report form"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <Textarea
              value={reportNote}
              onChange={(e) => setReportNote(e.target.value)}
              placeholder="E.g., line 4 should say '...' instead of '...'"
              rows={3}
              maxLength={2000}
              data-testid="report-lyrics-textarea"
            />
            <div className="flex justify-end">
              <Button
                onClick={submitReport}
                disabled={reporting || !reportNote.trim()}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="submit-lyrics-report-btn"
              >
                {reporting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
                ) : (
                  "Send report"
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
            </div>
          )}
          {locked && (
            <div
              className="my-4 rounded-lg border border-purple-200 bg-purple-50 p-5 text-center space-y-3"
              data-testid="lyrics-paywall"
            >
              <Lock className="h-6 w-6 mx-auto text-purple-600" aria-hidden="true" />
              <p className="text-sm font-medium text-purple-900" data-testid="lyrics-paywall-message">
                Lyrics are available to paid subscribers only.
              </p>
              <p className="text-xs text-purple-800/80 leading-relaxed">
                Every song includes an auto-transcribed lyric sheet for accessibility,
                unlocked once your subscription is confirmed.
              </p>
              <div className="flex justify-center gap-2 pt-1">
                <Button
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700"
                  onClick={() => { onOpenChange(false); navigate("/subscribe"); }}
                  data-testid="lyrics-paywall-subscribe-btn"
                >
                  Subscribe
                </Button>
                {locked === "guest" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-purple-400 text-purple-700"
                    onClick={() => { onOpenChange(false); navigate("/login"); }}
                    data-testid="lyrics-paywall-login-btn"
                  >
                    Log in
                  </Button>
                )}
              </div>
            </div>
          )}
          {error && (
            <p className="py-6 text-sm text-muted-foreground text-center" data-testid="lyrics-error">
              {error}
            </p>
          )}
          {data && !loading && (
            <pre
              className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground py-3 select-text"
              data-testid="lyrics-body"
            >
              {data.lyrics}
            </pre>
          )}
        </div>

        {/* Per-user visible watermark footer */}
        {data && (
          <div
            className="mt-2 pt-2 border-t border-purple-200 text-[10px] text-muted-foreground text-center"
            data-testid="lyrics-watermark"
          >
            {data.watermark}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
